import { create } from 'zustand';
import { FirestoreService } from '../services/firestore';

export const useAssessmentDocStore = create((set, get) => ({
  // State
  currentSubmission: null,
  responses: {},
  timeRemainingSeconds: null,
  saveStatus: 'idle', // 'idle' | 'saving' | 'saved' | 'error'
  lastSavedAt: null,
  isStarted: false,
  isSubmitted: false,
  isLoading: false,
  error: null,

  // Load existing submission for a document + learner
  loadSubmission: async (documentId, learnerId) => {
    try {
      set({ isLoading: true, error: null });
      const submission = await FirestoreService.getSubmissionByDocAndLearner(documentId, learnerId);
      if (submission) {
        const isSubmitted = submission.status === 'submitted' || submission.status === 'timed-out';
        set({
          currentSubmission: submission,
          responses: submission.responses || {},
          isStarted: true,
          isSubmitted,
          isLoading: false,
        });
        return submission;
      }
      set({ currentSubmission: null, responses: {}, isStarted: false, isSubmitted: false, isLoading: false });
      return null;
    } catch (error) {
      console.error('Error loading submission:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Start a new assessment attempt
  startAssessment: async (documentId, learnerInfo, assessmentConfig = {}, documentMeta = {}) => {
    try {
      set({ isLoading: true, error: null });
      const teacherAssignment = learnerInfo?.teacherAssignment || null;
      const tutorAssignment = learnerInfo?.tutorAssignment || null;
      const category = assessmentConfig?.category || 'formal';
      const informalType = assessmentConfig?.informalType || null;
      const requiresGrading = category === 'formal' || informalType === 'class-practice';
      const submissionData = {
        documentId,
        learnerId: learnerInfo.uid,
        learnerName: learnerInfo.displayName || '',
        learnerEmail: learnerInfo.email || '',
        documentTitle: documentMeta?.title || documentMeta?.name || '',
        documentName: documentMeta?.name || documentMeta?.title || '',
        documentOwnerId: documentMeta?.userId || null,
        documentOwnerName: documentMeta?.ownerName || documentMeta?.userName || '',
        assessmentCategory: category,
        assessmentType: informalType || null,
        requiresGrading,
        submittedToTeacherId: teacherAssignment?.userId || null,
        submittedToTeacherName: teacherAssignment?.displayName || '',
        submittedToTutorId: tutorAssignment?.userId || null,
        submittedToTutorName: tutorAssignment?.displayName || '',
        teacherAssignmentStatus: teacherAssignment?.status || null,
        tutorAssignmentStatus: tutorAssignment?.status || null,
        gradingStatus: requiresGrading ? 'pending-submission' : 'not-required',
        status: 'in-progress',
        startedAt: new Date().toISOString(),
        submittedAt: null,
        responses: {},
        timeRemainingSeconds: assessmentConfig?.timeLimit ? assessmentConfig.timeLimit * 60 : null,
      };
      const submissionId = await FirestoreService.createSubmission(submissionData);
      const submission = { id: submissionId, ...submissionData };
      set({
        currentSubmission: submission,
        responses: {},
        isStarted: true,
        isSubmitted: false,
        isLoading: false,
        timeRemainingSeconds: submissionData.timeRemainingSeconds,
      });
      return submission;
    } catch (error) {
      console.error('Error starting assessment:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Update a single response
  updateResponse: (blockId, value) => {
    set((state) => ({
      responses: {
        ...state.responses,
        [blockId]: { value, updatedAt: new Date().toISOString() },
      },
      saveStatus: 'idle',
    }));
  },

  // Save responses to Firestore
  saveResponses: async () => {
    const { currentSubmission, responses, isSubmitted } = get();
    if (!currentSubmission || isSubmitted) return;
    try {
      set({ saveStatus: 'saving' });
      await FirestoreService.updateSubmission(currentSubmission.id, { responses });
      set({ saveStatus: 'saved', lastSavedAt: new Date() });
    } catch (error) {
      console.error('Error saving responses:', error);
      set({ saveStatus: 'error' });
    }
  },

  // Submit the assessment
  submitAssessment: async () => {
    const { currentSubmission, responses } = get();
    if (!currentSubmission) return;
    try {
      set({ saveStatus: 'saving' });
      await FirestoreService.updateSubmission(currentSubmission.id, {
        responses,
        status: 'submitted',
        submittedAt: new Date().toISOString(),
        gradingStatus: currentSubmission.requiresGrading ? 'submitted' : currentSubmission.gradingStatus || 'not-required',
      });
      set({
        isSubmitted: true,
        saveStatus: 'saved',
        lastSavedAt: new Date(),
        currentSubmission: {
          ...currentSubmission,
          status: 'submitted',
          gradingStatus: currentSubmission.requiresGrading ? 'submitted' : currentSubmission.gradingStatus || 'not-required',
        },
      });
    } catch (error) {
      console.error('Error submitting assessment:', error);
      set({ saveStatus: 'error' });
      throw error;
    }
  },

  // Timer tick (called every second)
  tickTimer: () => {
    const { timeRemainingSeconds, isSubmitted } = get();
    if (timeRemainingSeconds === null || isSubmitted) return;
    const next = timeRemainingSeconds - 1;
    if (next <= 0) {
      set({ timeRemainingSeconds: 0 });
      // Auto-submit on time expiry
      get().submitAssessment().catch(() => {});
    } else {
      set({ timeRemainingSeconds: next });
    }
  },

  // Reset store
  reset: () => {
    set({
      currentSubmission: null,
      responses: {},
      timeRemainingSeconds: null,
      saveStatus: 'idle',
      lastSavedAt: null,
      isStarted: false,
      isSubmitted: false,
      isLoading: false,
      error: null,
    });
  },
}));
